import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import { Transforms, useEditor } from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import { isEntity, isPersisted } from '@tunasong/schemas'
import { ReactEditor } from 'slate-react'
import { resolveDiscardTarget, resolveRestorePath } from './discard-target.js'
import { removeBlockAndRepairSelection } from './repair-selection.js'
import { showScaffoldUndo } from './scaffold-undo-store.js'

/**
 * Discard a sketch: destructive-with-undo, not confirm-first.
 *
 * A confirmation dialog would contradict the whole point — it tells the user this placeholder is
 * precious, when throwing scaffolding away is the expected outcome rather than an error. What
 * makes that safe is a real Undo, and a soft delete: the entity is marked `trash`, never removed,
 * so restoring it is a fresh transform rather than a history pop.
 *
 * Marking `trash` also covers a discard while generation is still in flight — the SQS worker's
 * late write-back lands on an entity the user has already binned rather than resurrecting a block.
 */
export const useDiscardScaffold = (element: CoreElement) => {
  const editor = useEditor()
  const updateEntity = graphHooks.useEntityUpdate()

  return () => {
    const path = ReactEditor.findPath(editor as never, element)
    if (!path) {
      logger.warn('Cannot discard sketch: no path for element', { type: element.type })
      return
    }

    /**
     * Generated media sits inside a `ref` wrapper, so the sketch as the user sees it is the
     * wrapper — discard and restore that, or Undo re-inserts into a parent that is gone.
     * Structured snapshot so undo re-inserts the block as it was, not a live reference.
     */
    const target = resolveDiscardTarget(editor, path)
    const snapshot = structuredClone(target.node)
    const entityId = isEntity(element) && isPersisted(element) ? element.id : null

    removeBlockAndRepairSelection(editor, target.at)
    if (entityId) {
      updateEntity(entityId, { trash: true })
    }

    showScaffoldUndo({
      elementType: element.type,
      message: 'Sketch discarded.',
      undo: () => {
        // Un-trash first: the entity must never be stranded in the trash by an editor transform
        // that fails, since the snackbar is already gone and there is no second Undo.
        if (entityId) {
          updateEntity(entityId, { trash: false })
        }
        Transforms.insertNodes(editor, snapshot as never, { at: resolveRestorePath(editor, target.at), select: true })
        showScaffoldUndo({ elementType: element.type, message: 'Sketch restored.' })
      },
    })
  }
}
